/* eslint-disable no-undef */
import { useState, useEffect, VFC, useRef } from 'react';
import { FloorplanWithBlob } from '../../hooks/useFloorplan';
import { useObjectUrl } from '../../hooks/useObjectUrl';
import { calculateImageBound } from '../../utils/calculateImageBounds';
import { FloorplanMarker, Marker } from './Marker';

function calculateImageBounds(floorplan: FloorplanWithBlob) {
  const { height, width } = floorplan;
  const lat = calculateImageBound(height);
  const lng = calculateImageBound(width);

  return {
    north: lat,
    south: -lat,
    east: lng,
    west: -lng,
  };
}

const initMap = (ref: HTMLDivElement) =>
  new window.google.maps.Map(ref, {
    zoom: 4,
    maxZoom: 20,
    center: { lat: 0, lng: 0 },
    mapTypeId: 'floorplan_coordinate',
    tilt: 0,
  });

export interface MapProps {
  floorplan: FloorplanWithBlob;
  editable?: boolean;
  markers?: FloorplanMarker[];
  onMarkerDragEnd?: (updatedMarker: FloorplanMarker) => void;
}

export const Map: VFC<MapProps> = ({ markers, floorplan, editable, onMarkerDragEnd }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  const backgroundUrl = useObjectUrl(floorplan.blob);

  useEffect(() => {
    if (!map && ref.current) {
      setMap(initMap(ref.current));
    }
  }, [map, ref]);

  useEffect(() => {
    if (!map) return () => {};

    const imageBounds = calculateImageBounds(floorplan);
    map.setOptions({
      restriction: {
        latLngBounds: imageBounds,
        strictBounds: false,
      },
    });
    const overlay = new window.google.maps.GroundOverlay(backgroundUrl, imageBounds);
    overlay.setMap(map);
    return () => overlay.setMap(null);
  }, [backgroundUrl, map, floorplan]);

  return (
    <div ref={ref} style={{ height: '70vh' }}>
      {markers?.map(({ id, lat, lng, title }) => (
        <Marker
          key={id}
          draggable={editable}
          id={id}
          map={map}
          position={{ lat, lng }}
          title={title}
          onDragEnd={onMarkerDragEnd}
        />
      ))}
    </div>
  );
};
